/**
 * CaptureMetadataModal — Phase 1B-I (2026-05-29 follow-up).
 *
 * Pops after a capture / freeze / whiteboard save lands a new Evidence
 * row. The operator gets a chance to give it a meaningful title (pre-
 * filled with "Snapshot YYYY-MM-DD HH:mm" or "Whiteboard YYYY-MM-DD
 * HH:mm" by default) and attach notes + keywords for later search.
 *
 * The modal posts a PATCH to /v1/evidence/<id>/metadata; the backend
 * mirrors the title onto the auto-posted ChatMessage body so the
 * chat sidebar updates without a refresh.
 *
 * Dismissing the modal (Esc or backdrop click) skips the PATCH —
 * the evidence row keeps its empty-title defaults and the chat card
 * renders the kind-default headline (e.g. "Snapshot captured"). That
 * way the operator never feels blocked by the modal; it's a
 * "let me name it" affordance, not a gate.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";


export type CaptureKind = "snapshot" | "whiteboard" | "annotation" | "recording";

type Props = {
  evidenceId: string;
  sessionId: string;
  /** Drives the default title prefix + the modal header. */
  kind: CaptureKind;
  /** Capture wall clock — defaults to now() if not provided. */
  capturedAt?: Date;
  /** Optional explicit default that overrides the auto-generated
   *  "Snapshot YYYY-MM-DD HH:mm" string. Used by the chat attachment
   *  flow to pre-fill with the original filename. */
  defaultTitleOverride?: string;
  /** When true, the modal can be auto-skipped if defaultTitleOverride
   *  is non-empty — caller handles the auto-confirm. Not used here. */
  onClose: () => void;
};


function defaultTitle(kind: CaptureKind, when: Date, t: TFunction): string {
  // ISO-ish, 24h, locale-agnostic — "Snapshot 2026-05-30 04:08". Easier
  // to sort + grep in audit logs than the locale-dependent variant.
  const pad = (n: number) => n.toString().padStart(2, "0");
  const stamp =
    `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())} ` +
    `${pad(when.getHours())}:${pad(when.getMinutes())}`;
  const prefix =
    kind === "whiteboard"
      ? t("ui.captureMetadataModal.prefixWhiteboard")
      : kind === "annotation"
        ? t("ui.captureMetadataModal.prefixAnnotation")
        : kind === "recording"
          ? t("ui.captureMetadataModal.prefixRecording")
          : t("ui.captureMetadataModal.prefixSnapshot");
  return `${prefix} ${stamp}`;
}


function modalHeader(kind: CaptureKind, t: TFunction): string {
  if (kind === "whiteboard") return t("ui.captureMetadataModal.headerWhiteboard");
  if (kind === "annotation") return t("ui.captureMetadataModal.headerAnnotation");
  if (kind === "recording") return t("ui.captureMetadataModal.headerRecording");
  return t("ui.captureMetadataModal.headerSnapshot");
}


export function CaptureMetadataModal({
  evidenceId,
  sessionId,
  kind,
  capturedAt,
  defaultTitleOverride,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [title, setTitle] = useState(
    () =>
      defaultTitleOverride ??
      defaultTitle(kind, capturedAt ?? new Date(), t),
  );
  const [notes, setNotes] = useState("");
  const [keywords, setKeywords] = useState("");

  // Esc dismisses without saving.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const save = useMutation({
    mutationFn: async () => {
      const res = await api.patch(ApiPaths.evidenceMetadata(evidenceId), {
        title: title.trim(),
        notes: notes.trim(),
        keywords: keywords.trim(),
      });
      return res.data;
    },
    onSuccess: () => {
      // Chat card body is updated server-side; invalidate so the SPA
      // picks up the new title without waiting on the poll interval.
      void qc.invalidateQueries({ queryKey: ["session-chat", sessionId] });
      void qc.invalidateQueries({ queryKey: ["session-evidence", sessionId] });
      onClose();
    },
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="capture-metadata-title"
      // No backdrop-click dismissal: the operator just captured an
      // artefact and a stray click outside the form would silently
      // drop the unsaved title/notes/keywords and keep the default
      // generated name forever (no second chance — the modal only
      // opens once per capture). Esc + the explicit Cancel button
      // remain the dismissal paths.
      className="modal-backdrop bg-black/60"
    >
      <div
        className="w-full max-w-md rounded-2xl border border-nb-border bg-nb-bg p-5 shadow-2xl"
      >
        <h2
          id="capture-metadata-title"
          className="mb-1 text-lg font-semibold text-nb-text"
        >
          {modalHeader(kind, t)}
        </h2>
        <p className="mb-4 text-xs text-nb-text-2">
          {t("ui.captureMetadataModal.intro")}
        </p>

        <label className="label mt-3" htmlFor="cm-title">
          {t("ui.captureMetadataModal.titleLabel")}
        </label>
        <input
          id="cm-title"
          type="text"
          className="input"
          value={title}
          maxLength={200}
          onChange={(e) => setTitle(e.target.value)}
          autoFocus
        />

        <label className="label mt-4" htmlFor="cm-notes">
          {t("ui.captureMetadataModal.notesLabel")}
        </label>
        <textarea
          id="cm-notes"
          className="input"
          rows={3}
          value={notes}
          maxLength={2000}
          placeholder={t("ui.captureMetadataModal.notesPlaceholder")}
          onChange={(e) => setNotes(e.target.value)}
        />

        <label className="label mt-4" htmlFor="cm-kw">
          {t("ui.captureMetadataModal.keywordsLabel")}
        </label>
        <input
          id="cm-kw"
          type="text"
          className="input"
          value={keywords}
          maxLength={500}
          placeholder={t("ui.captureMetadataModal.keywordsPlaceholder")}
          onChange={(e) => setKeywords(e.target.value)}
        />

        {save.isError && (
          <p role="alert" className="mt-3 text-xs text-cvp-danger">
            {t("ui.captureMetadataModal.saveError", {
              message:
                (save.error as Error)?.message ??
                t("ui.captureMetadataModal.saveErrorFallback"),
            })}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>
            {t("ui.captureMetadataModal.skip")}
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!title.trim() || save.isPending}
            onClick={() => save.mutate()}
            data-testid="capture-metadata-save"
          >
            {save.isPending
              ? t("ui.captureMetadataModal.saving")
              : t("ui.captureMetadataModal.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
